/**
 * Lightweight web projection of the canonical rashi registry.
 *
 * Domain facts come from the generated @ishvaram/domain-data package. This
 * module owns only web presentation fields and input aliases, so lightweight
 * client surfaces do not need to import the compatibility engine in rashi.ts.
 */

import {
  planets as canonicalPlanets,
  rashis as canonicalRashis,
  type Rashi as CanonicalRashi,
} from "@ishvaram/domain-data";

export interface Rashi extends Omit<CanonicalRashi, "element" | "quality"> {
  element: "fire" | "earth" | "air" | "water";
  quality: "Cardinal" | "Fixed" | "Mutable";
  ruler_en: string;
  zodiac_symbol: string;
}

export interface RashiInfo {
  hi: string;
  symbol: string;
  ruler: string;
  element: string;
  quality: string;
}

const ZODIAC_SYMBOLS = [
  "♈",
  "♉",
  "♊",
  "♋",
  "♌",
  "♍",
  "♎",
  "♏",
  "♐",
  "♑",
  "♒",
  "♓",
] as const;

const PLANET_NAMES = Object.fromEntries(
  canonicalPlanets.map((planet) => [planet.id, planet.name_en])
) as Readonly<Record<string, string>>;

function capitalized(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function toWebRashi(rashi: CanonicalRashi): Rashi {
  const zodiacSymbol = ZODIAC_SYMBOLS[rashi.number - 1];
  if (!zodiacSymbol) throw new Error(`Canonical rashi number is outside 1..12: ${rashi.number}`);

  const element = rashi.element.toLowerCase();
  if (!(["fire", "earth", "air", "water"] as const).includes(element as Rashi["element"])) {
    throw new Error(`Canonical rashi has unsupported element: ${rashi.id}/${rashi.element}`);
  }

  const quality = capitalized(rashi.quality);
  if (!(["Cardinal", "Fixed", "Mutable"] as const).includes(quality as Rashi["quality"])) {
    throw new Error(`Canonical rashi has unsupported quality: ${rashi.id}/${rashi.quality}`);
  }

  return {
    ...rashi,
    element: element as Rashi["element"],
    quality: quality as Rashi["quality"],
    ruler_en: PLANET_NAMES[rashi.ruler] ?? rashi.ruler,
    zodiac_symbol: zodiacSymbol,
    gender: capitalized(rashi.gender),
  };
}

export const RASHIS: Rashi[] = canonicalRashis.map(toWebRashi);

export const RASHI_MAP: Record<string, Rashi> = Object.fromEntries(
  RASHIS.map((rashi) => [rashi.id, rashi])
);

export const RASHI_EMOJIS: Record<string, string> = Object.fromEntries(
  RASHIS.map((rashi) => [rashi.id, rashi.zodiac_symbol])
);

const RASHI_ALIASES: Readonly<Record<string, string>> = Object.fromEntries(
  RASHIS.flatMap((rashi) =>
    [rashi.id, rashi.name_en, rashi.name_sa].map((name) => [name.toLowerCase(), rashi.id])
  )
);

export function resolveRashi(value?: string | null): Rashi | undefined {
  const normalized = (value ?? "").trim().toLowerCase();
  if (!normalized) return undefined;
  return RASHI_MAP[RASHI_ALIASES[normalized] ?? normalized];
}

export function isKnownRashi(value?: string | null): boolean {
  return resolveRashi(value) !== undefined;
}

export function resolveRashiInfo(value?: string | null): RashiInfo | undefined {
  const rashi = resolveRashi(value);
  if (!rashi) return undefined;
  return {
    hi: rashi.name_hi,
    symbol: rashi.zodiac_symbol,
    ruler: rashi.ruler_en,
    element: capitalized(rashi.element),
    quality: rashi.quality,
  };
}
